import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'react-toastify';

import GhgFuelTable from 'components/GhgEmissionReport/GhgFuelTable';
import GhgOverview from 'components/GhgEmissionReport/GhgOverview';
import GhgPeriodPicker from 'components/GhgEmissionReport/GhgPeriodPicker';
import GhgScenarios from 'components/GhgEmissionReport/GhgScenarios';
import { IGhgEmissionReport } from 'models/ghg-emission-report';
import { AuthenticationContext } from 'services/context/authentication.context';
import GhgEmissionReportService from 'services/ghg-emission-report.service';

const MAX_PERIOD_MONTHS = 12;

function defaultPeriodStart() {
    return moment().startOf('month').subtract(MAX_PERIOD_MONTHS, 'month').format('YYYY-MM');
}

function defaultPeriodEnd() {
    return moment().startOf('month').subtract(1, 'month').format('YYYY-MM');
}

/** The period as the report heading shows it, e.g. "01.01.2026-31.08.2026". */
function periodLabel(periodStart: string, periodEnd: string) {
    const start = moment(periodStart, 'YYYY-MM').startOf('month').format('DD.MM.YYYY');
    const end = moment(periodEnd, 'YYYY-MM').endOf('month').format('DD.MM.YYYY');
    return start + '–' + end;
}

/** Mirrors the server-side period validation so the user is told before a request is made. */
function validatePeriod(periodStart: string, periodEnd: string): string | null {
    const start = moment(periodStart, 'YYYY-MM');
    const end = moment(periodEnd, 'YYYY-MM');

    if (start.isAfter(end)) return 'Lõpukuu ei tohi olla enne alguskuud.';
    if (end.diff(start, 'months') + 1 > MAX_PERIOD_MONTHS) {
        return `Periood ei tohi olla pikem kui ${MAX_PERIOD_MONTHS} kuud.`;
    }

    return null;
}

export default function GhgEmissionReport(props: any) {
    const context: any = useContext(AuthenticationContext);
    const customerId: number | null = context.state.customerId;

    const [periodStart, setPeriodStart] = useState(defaultPeriodStart);
    const [periodEnd, setPeriodEnd] = useState(defaultPeriodEnd);
    const [report, setReport] = useState<IGhgEmissionReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState<'PDF' | 'EXCEL'>();
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [scenariosVisible, setScenariosVisible] = useState(false);

    // The period the currently rendered report was built for, so the heading keeps matching the figures
    // when the user changes the selects without regenerating.
    const [reportPeriod, setReportPeriod] = useState({ periodStart, periodEnd });

    // Registered during the first render, as the class pages do from their constructor: the route guard
    // reads the active page's rules on that same render, so deferring to an effect leaves it a render behind.
    useState(() => props.onLoadValidatePage());

    const error = validatePeriod(periodStart, periodEnd);

    // Judged on consumption rather than emission: a customer who only charged electric vehicles has real
    // data to report even though it emits nothing.
    const hasData = !!report && report.fuels.some(fuel => fuel.quantity > 0);

    const generate = useCallback((start: string, end: string) => {
        if (customerId === null || validatePeriod(start, end)) return;

        setLoading(true);
        GhgEmissionReportService.getGhgEmissionReport(customerId, start, end)
            .then((response: IGhgEmissionReport) => {
                setReport(response);
                setReportPeriod({ periodStart: start, periodEnd: end });
            })
            .catch(() => {
                setReport(null);
                toast.error('KHG-raporti koostamine ebaõnnestus');
            })
            .finally(() => setLoading(false));
    }, [customerId]);

    // Load the default period once the customer is known, and reset back to it when the customer switches.
    // Keyed on customerId alone: including `generate` would re-run this whenever it is recreated and undo
    // the period the user has since chosen.
    useEffect(() => {
        if (customerId === null) return;

        const start = defaultPeriodStart();
        const end = defaultPeriodEnd();

        setPeriodStart(start);
        setPeriodEnd(end);
        generate(start, end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const download = (type: 'PDF' | 'EXCEL') => {
        if (customerId === null || downloading) return;

        setDownloading(type);

        const request = type === 'PDF'
            ? GhgEmissionReportService.downloadGhgEmissionReportPdf
            : GhgEmissionReportService.downloadGhgEmissionReportExcel;

        request(customerId, reportPeriod.periodStart, reportPeriod.periodEnd)
            .catch(() => toast.error('KHG-raporti allalaadimine ebaõnnestus'))
            .finally(() => setDownloading(undefined));
    };

    return (
        <div className="ghg-report">
            <div className="ghg-report__header">
                <div className="ghg-report__heading">
                    <h2 className="mb-1">Transpordikütuste KHG-heite raport</h2>
                    <p className="u-fs-14 ghg-muted mb-0">
                        {report ? report.companyName + ' | ' : ''}
                        {periodLabel(reportPeriod.periodStart, reportPeriod.periodEnd)}
                    </p>
                    <p className="u-fs-14 ghg-muted mb-0 mt-2">
                        Raport annab ülevaate valitud perioodil transpordikütuste kasutamisest tekkinud
                        kasvuhoonegaaside (KHG) heitest ning selle vähendamise võimalustest.
                    </p>
                </div>
                <div className="ghg-report__actions">
                    <Button
                        variant="primary"
                        className="ghg-submit mr-2"
                        onClick={() => download('PDF')}
                        disabled={!hasData || !!downloading}
                    >
                        <span className={'px-2' + (downloading === 'PDF' ? ' invisible' : '')}>PDF</span>
                        {downloading === 'PDF'
                            ? <Spinner size="sm" animation="border" className="ghg-submit__spinner" />
                            : null}
                    </Button>
                    <Button
                        variant="primary"
                        className="ghg-submit"
                        onClick={() => download('EXCEL')}
                        disabled={!hasData || !!downloading}
                    >
                        <span className={'px-2' + (downloading === 'EXCEL' ? ' invisible' : '')}>Excel</span>
                        {downloading === 'EXCEL'
                            ? <Spinner size="sm" animation="border" className="ghg-submit__spinner" />
                            : null}
                    </Button>
                </div>
            </div>

            <GhgPeriodPicker
                periodStart={periodStart}
                periodEnd={periodEnd}
                onPeriodStartChange={setPeriodStart}
                onPeriodEndChange={setPeriodEnd}
                onSubmit={() => generate(periodStart, periodEnd)}
                loading={loading}
                error={error}
            />

            {loading && !report ? (
                <div className="card mb-4">
                    <div className="card-body text-center py-5">
                        <Spinner animation="border" />
                    </div>
                </div>
            ) : null}

            {report && !hasData ? (
              <div className="ghg-empty">
                  <h3 className="u-fs-18 font-weight-bold mb-2">Valitud perioodil andmed puuduvad</h3>
                  <p className="u-fs-14 ghg-muted mb-0">
                      Sellel perioodil ei ole Terminalis tarbimist, mille põhjal KHG-heite raportit koostada.
                      Vali teine periood.
                  </p>
              </div>
            ) : null }

            {hasData && report ? (
                <>
                    <div className="card mb-4">
                        <div className="card-body">
                            <GhgOverview report={report} />
                        </div>
                    </div>

                    <div className="card mb-4">
                        <div className="card-body">
                            <Button
                                variant="outline-primary"
                                className={detailsVisible ? 'mb-4' : ''}
                                onClick={() => setDetailsVisible(!detailsVisible)}
                            >
                                <span className="px-2">
                                    {detailsVisible ? 'Peida detailne ülevaade' : 'Vaata detailset ülevaadet'}
                                </span>
                            </Button>
                            {detailsVisible
                                ? <GhgFuelTable fuels={report.fuels} actualEmission={report.actualEmission} />
                                : null}
                        </div>
                    </div>

                    <div className="card mb-4">
                        <div className="card-body">
                            <Button
                                variant="outline-primary"
                                className={scenariosVisible ? 'mb-4' : ''}
                                onClick={() => setScenariosVisible(!scenariosVisible)}
                            >
                                <span className="px-2">
                                    {scenariosVisible
                                        ? 'Peida KHG-heite vähendamise võimalused'
                                        : 'Vaata, kuidas KHG-heidet täiendavalt vähendada'}
                                </span>
                            </Button>
                            {scenariosVisible ? <GhgScenarios report={report} /> : null}
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
}
